import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { readLocation } from "@/components/LocationPill";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import WeatherPulseNav from "./WeatherPulseNav";
import { ArrowRight, GraduationCap, Lock, MapPin, Plus, ShieldCheck, Users } from "@phosphor-icons/react";

const INTRO = `The Schools Programme turns a classroom into a weather station. Teachers register a station, students take daily readings, and the whole school joins the Observe Weather 365 challenge — learning real science with real data.`;

const PROMISES = [
  { icon: Users, bg: "#1B4332", t: "No student accounts or names", d: "Only a teacher registers. The public page shows the school and its weather — never a child." },
  { icon: MapPin, bg: "#0C4A6E", t: "Location shown on a coarse grid", d: "School coordinates are published rounded to ~11 km, not the exact address." },
  { icon: Lock, bg: "#155E75", t: "Teacher-run, always", d: "Reports from a school station are posted under the school's programme identity by the registered teacher." },
];

const GRADES = ["K-2", "3-5", "6-8", "9-12"];
const PROGRAMS = ["Weather 365", "Cloud spotting", "Rain gauge diary", "Climate watch"];
const INSTRUMENTS = ["Thermometer", "Rain gauge", "Barometer", "Hygrometer", "Anemometer", "Wind vane"];

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function WeatherSchools() {
  const { user, requireSignIn } = useAuth();
  const [schools, setSchools] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ school_name: "", city: "", state: "", country: "", grade_levels: [], programs: [], instruments: [] });

  const loadSchools = () => http.get("/weather-pulse/schools").then(({ data }) => setSchools(data.schools || [])).catch(() => setSchools([]));
  useEffect(() => { loadSchools(); }, []);

  const register = async () => {
    if (!requireSignIn("register a classroom weather station")) return;
    if (f.school_name.trim().length < 3) return toast.error("Enter the school name");
    const loc = readLocation();
    if (!loc?.lat) return toast.error("Set your location first (the location pill in the header)");
    try {
      await http.post("/weather-pulse/schools/register", {
        school_name: f.school_name.trim(),
        city: f.city || loc.city || null,
        state: f.state || loc.state || null,
        country: f.country || loc.country || null,
        grade_levels: f.grade_levels,
        programs: f.programs,
        instruments: f.instruments,
        lat: loc.lat, lon: loc.lon,
      });
      toast.success("Classroom station registered — welcome aboard!");
      setShowForm(false);
      setF({ school_name: "", city: "", state: "", country: "", grade_levels: [], programs: [], instruments: [] });
      loadSchools();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't register the station");
    }
  };

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-schools">
      <div className="max-w-[1000px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2 flex items-center gap-1.5">
          <GraduationCap size={13} weight="fill" /> / Schools programme
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.0] tracking-tight">Your classroom,<br />a weather station.</h1>
        <p className="mt-3 max-w-2xl text-[#3A3830] text-sm md:text-base">{INTRO}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <ListenTranslateBar text={INTRO} testidPrefix="wp-schools-tools" />
          <Link to="/weather-pulse/schools/journal" data-testid="wp-schools-journal-link"
            className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#1B4332] hover:gap-2 transition-all">
            Classroom journal →
          </Link>
        </div>
        <div className="mt-5"><PageGuide pageKey="weather:schools" testidPrefix="wp-schools" /></div>

        {/* Student-safe promises */}
        <div className="mt-8 grid gap-3 md:grid-cols-3" data-testid="wp-schools-promises">
          {PROMISES.map((p) => (
            <div key={p.t} className="rounded-2xl text-[#F5EBDC] p-5" style={{ backgroundColor: p.bg }}>
              <p.icon size={20} weight="fill" className="text-[#E4A15C]" />
              <div className="mt-2 font-serif-display text-lg leading-tight">{p.t}</div>
              <p className="mt-1.5 text-xs text-[#E5D8C4]/85">{p.d}</p>
            </div>
          ))}
        </div>

        {/* Register */}
        {!user ? (
          <button onClick={() => requireSignIn("register a classroom weather station")} data-testid="wp-schools-signin"
            className="mt-8 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
            Sign in as a teacher to register
          </button>
        ) : !showForm ? (
          <button onClick={() => setShowForm(true)} data-testid="wp-schools-new"
            className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
            <Plus size={13} weight="bold" /> Register your classroom station
          </button>
        ) : (
          <div className="mt-8 rounded-2xl border border-black/10 bg-white p-5" data-testid="wp-schools-form">
            <input value={f.school_name} onChange={(e) => setF({ ...f, school_name: e.target.value })}
              placeholder="School name — e.g. Green Valley Public School" data-testid="wp-schools-name"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
            <div className="mt-3 grid grid-cols-3 gap-2">
              <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City" data-testid="wp-schools-city" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
              <input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} placeholder="State" data-testid="wp-schools-state" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
              <input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} placeholder="Country" data-testid="wp-schools-country" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
            </div>
            {[
              ["Grade levels", "grade_levels", GRADES, "grade"],
              ["Programmes", "programs", PROGRAMS, "program"],
              ["Instruments", "instruments", INSTRUMENTS, "inst"],
            ].map(([label, key, opts, tid]) => (
              <div key={key} className="mt-4">
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{label}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {opts.map((o) => (
                    <button key={o} type="button" onClick={() => setF({ ...f, [key]: toggle(f[key], o) })}
                      data-testid={`wp-schools-${tid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.18em] uppercase border ${f[key].includes(o) ? "bg-[#1B4332] text-[#F5EBDC] border-transparent" : "bg-white border-black/15"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <p className="mt-3 text-[11px] text-[#3A3830]/70 flex items-center gap-1">
              <ShieldCheck size={12} weight="fill" /> Location: your saved location, published on a coarse ~11 km grid only. No student names are ever collected.
            </p>
            <div className="mt-4 flex gap-2">
              <button onClick={register} data-testid="wp-schools-save"
                className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Register station</button>
              <button onClick={() => setShowForm(false)} data-testid="wp-schools-cancel"
                className="rounded-full border border-black/15 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Cancel</button>
            </div>
          </div>
        )}

        {/* Directory */}
        <div className="mt-12" data-testid="wp-schools-directory">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">
            Classroom stations on the network
          </div>
          {!schools ? (
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
          ) : schools.length === 0 ? (
            <p className="text-sm text-[#3A3830]" data-testid="wp-schools-empty">No classroom stations yet — yours could be the first.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {schools.map((s) => (
                <Link key={s.id} to={`/weather-pulse/station/${s.id}`} data-testid={`wp-school-${s.id}`}
                  className="group rounded-2xl bg-white border border-black/10 p-5 hover:border-[color:var(--np-brand,#B85F2A)] hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-serif-display text-xl leading-tight">{s.name}</div>
                    <ArrowRight size={13} weight="bold" className="text-[color:var(--np-brand,#B85F2A)] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/70">
                    {[s.city, s.country].filter(Boolean).join(", ")} · {s.observation_count} observations
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(s.school?.grade_levels || []).map((g) => (
                      <span key={g} className="rounded-full border border-black/10 px-2 py-0.5 font-mono-tech text-[8px] tracking-[0.16em] uppercase">Grades {g}</span>
                    ))}
                    {(s.school?.programs || []).map((p) => (
                      <span key={p} className="rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] px-2 py-0.5 font-mono-tech text-[8px] tracking-[0.16em] uppercase">{p}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
